// ─── Creative Types ─────────────────────────────────────────────────────────

export enum CreativeType {
  InstagramPost = 'instagram_post',
  FacebookPost = 'facebook_post',
  TikTokVideo = 'tiktok_video',
  LinkedInPost = 'linkedin_post',
  XPost = 'x_post',
  YouTubeShort = 'youtube_short',
  PinterestPin = 'pinterest_pin',
}

/** Map CreativeType → backend creatorMode string used by /v1/creative/chat */
export const CREATOR_MODE_MAP: Record<CreativeType, string> = {
  [CreativeType.InstagramPost]: 'instagram',
  [CreativeType.FacebookPost]: 'facebook',
  [CreativeType.TikTokVideo]: 'tiktok',
  [CreativeType.LinkedInPost]: 'linkedin',
  [CreativeType.XPost]: 'x',
  [CreativeType.YouTubeShort]: 'youtube_shorts',
  [CreativeType.PinterestPin]: 'pinterest',
}

export enum CreativeStatus {
  Draft = 'draft',
  Published = 'published',
}

// ─── Platform Classification ────────────────────────────────────────────────

/** Social post types can be cross-published from a single prompt */
export const SOCIAL_POST_TYPES = new Set([
  CreativeType.InstagramPost,
  CreativeType.FacebookPost,
  CreativeType.TikTokVideo,
  CreativeType.LinkedInPost,
  CreativeType.XPost,
  CreativeType.YouTubeShort,
  CreativeType.PinterestPin,
])

export function isSocialPostType(type: CreativeType): boolean {
  return SOCIAL_POST_TYPES.has(type)
}

// ─── Shared Constants ──────────────────────────────────────────────────────

export type VideoStatus = 'generating' | 'ready' | 'failed'

export const MAX_HASHTAGS = 5

export const VIDEO_PROGRESS_MESSAGES = [
  'Creating video...',
  'Still working on it...',
  'Generating frames...',
  'Processing footage...',
  'Almost there...',
  'Putting it all together...',
  'Still processing, hang tight...',
]

export const POST_PROGRESS_MESSAGES = [
  'Drafting your post…',
  'Picking the right image…',
  'Writing your caption…',
  'Almost there…',
]

export const CAROUSEL_PROGRESS_MESSAGES = [
  'Drafting your carousel…',
  'Designing the slides…',
  'Writing your caption…',
  'Almost there…',
]

// ─── Project ────────────────────────────────────────────────────────────────

export interface Project {
  id: string
  name: string
  originalPrompt: string | null
  targetPlatforms: CreativeType[]
  creativeIds: string[]
  createdAt: string
  updatedAt: string
}

// ─── Content Schemas (per creative type) ────────────────────────────────────

/**
 * Base fields shared by every platform's content shape.
 *
 * `compositionPendingRender` — true while the layered Composition carries edits
 * newer than the last-rendered flat master (`imageUrl` / `mediaItems`). Set by
 * the server when composition edits are saved (dirty doc) and cleared when the
 * master re-renders. Master-image surfaces use it to show the "Applying your
 * edits" chip instead of silently presenting the stale pre-edit design. Never
 * set for video-shape posts (a reel's master only renders on explicit request).
 *
 * `compositionRenderFailed` — the edit pipeline's terminal state: the master
 * render did not land after its retries (editor exit render, AI revise, the
 * publish-time render, or the server reaper). Stamped by the server next to a
 * still-true `compositionPendingRender`; the chip flips from "Applying your
 * edits" to "Edits didn't apply" with a free Retry that re-renders the stored
 * document. Cleared with `null` (never key removal, so the Pusher content patch
 * carries the clearing) when a render starts over or lands. `reason` is a
 * machine token for the error inbox (`unrenderable`, `render_error`,
 * `render_unavailable`, `stuck`); the UI never shows it.
 */
export interface BaseCreativeContent {
  compositionPendingRender?: boolean
  compositionRenderFailed?: { at: string; reason: string } | null
}

/** Base type shared by all platform publish metadata (see also server *-publish.types.ts) */
export interface BasePublishMetadata {
  lastPublishedAt: string
  contentHash?: string
}

export interface InstagramPublishMetadata extends BasePublishMetadata {
  permalink?: string
  mediaId?: string
  mediaType?: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM'
  childCount?: number
}

export interface InstagramMediaItem {
  attachmentId: string
  url: string
  type: 'image' | 'video'
  altText?: string
  ratio?: number
  mimeType?: string
}

export interface InstagramPostContent extends BaseCreativeContent {
  caption: string
  hashtags: string[]
  imageUrl: string | null
  imageAttachmentId?: string | null
  /** Real image aspect ratio (width/height), re-derived from the attachment on
   *  read so the preview frame matches the image instead of guessing. */
  imageRatio?: number
  imagePrompt: string | null
  format: 'square' | 'portrait' | 'landscape' | 'story' | 'reel'
  altText: string
  videoUrl?: string | null
  videoAttachmentId?: string | null
  videoStatus?: VideoStatus
  mediaItems?: InstagramMediaItem[]
  instagramPublish?: InstagramPublishMetadata
}

export interface FacebookPublishMetadata extends BasePublishMetadata {
  permalink?: string
  postId?: string
  /** Set to 'carousel' when published as a multi-photo album, 'reel' when published as a Page Reel. */
  mediaType?: 'carousel' | 'reel'
  /** Number of slides published in the carousel album. */
  childCount?: number
  /** FB Page Reel video_id (returned by the /video_reels start step). Used to fetch permalink + poll status. */
  videoId?: string
}

export interface FacebookPostContent extends BaseCreativeContent {
  message: string
  imageUrl: string | null
  imageAttachmentId?: string | null
  imageRatio?: number
  imagePrompt: string | null
  altText: string
  /** Carousel slides — present when this Facebook post is a multi-photo album sibling of an Instagram carousel. */
  mediaItems?: InstagramMediaItem[]
  /** Source video for a Reel. Populated by generate-video; published via /video_reels. */
  videoUrl?: string | null
  videoAttachmentId?: string | null
  /** 'generating' | 'ready' | 'failed' — drives the IdeaCard + preview overlay during the Kling render window. */
  videoStatus?: VideoStatus
  facebookPublish?: FacebookPublishMetadata
}

// ─── TikTok Types ──────────────────────────────────────────────────────────

export interface TikTokPublishMetadata extends BasePublishMetadata {
  publishId?: string
}

/** TikTok's audience options, as returned by its creator_info query. */
export type TikTokPrivacyLevel =
  'PUBLIC_TO_EVERYONE' | 'MUTUAL_FOLLOW_FRIENDS' | 'FOLLOWER_OF_CREATOR' | 'SELF_ONLY'

/**
 * Per-post choices TikTok requires the USER to make before a Direct Post: the
 * audience (from the creator's own options, no default), interaction toggles
 * (off by default) and commercial-content disclosure (opt-in). Mirrors the
 * server's TikTokPostSettings; persisted on the creative so scheduled and
 * autopilot publishes carry them.
 */
export interface TikTokPostSettings {
  privacyLevel: TikTokPrivacyLevel
  allowComment: boolean
  allowDuet: boolean
  allowStitch: boolean
  commercialContent: boolean
  /** "Your brand": promotes the creator's own business. */
  brandOrganic: boolean
  /** "Branded content": paid partnership with a third party. */
  brandedContent: boolean
}

/** Where the video came from. Uploaded = the customer's own footage (no AI label on TikTok). */
export type TikTokVideoSource = 'uploaded' | 'generated'

export interface TikTokVideoContent extends BaseCreativeContent {
  caption: string
  hashtags: string[]
  videoUrl: string | null
  videoAttachmentId?: string | null
  videoStatus?: VideoStatus
  videoSource?: TikTokVideoSource
  /** Required to publish. See TikTokPostSettings. */
  postSettings?: TikTokPostSettings
  /** Source image used to generate the video. Doubles as a poster while
   *  videoStatus === 'generating'. NOT published to TikTok — only the final
   *  video is. */
  imageUrl?: string | null
  imageAttachmentId?: string | null
  tiktokPublish?: TikTokPublishMetadata
}

// ─── LinkedIn Types ─────────────────────────────────────────────────────────

export interface LinkedInPublishMetadata extends BasePublishMetadata {
  postUrn?: string
  permalink?: string
}

export type LinkedInPostFormat = 'square' | 'landscape' | 'portrait'

export interface LinkedInPostContent extends BaseCreativeContent {
  caption: string
  hashtags: string[]
  imageUrl: string | null
  imageAttachmentId?: string | null
  imageRatio?: number
  imagePrompt: string | null
  format: LinkedInPostFormat
  altText: string
  videoUrl?: string | null
  videoAttachmentId?: string | null
  videoStatus?: VideoStatus
  linkedinPublish?: LinkedInPublishMetadata
}

// ─── X (Twitter) Types ─────────────────────────────────────────────────────

export interface XPublishMetadata extends BasePublishMetadata {
  tweetId?: string
  /** `https://x.com/i/web/status/<tweetId>`; set by the server since 2026-09-06. */
  permalink?: string
}

export interface XPostContent extends BaseCreativeContent {
  text: string
  hashtags: string[]
  imageUrl: string | null
  imageAttachmentId?: string | null
  imageRatio?: number
  imagePrompt: string | null
  xPublish?: XPublishMetadata
}

// ─── YouTube Shorts Types ───────────────────────────────────────────────────

export interface YouTubePublishMetadata extends BasePublishMetadata {
  videoId?: string
  permalink?: string
}

export interface YouTubeShortsContent extends BaseCreativeContent {
  title: string
  description: string
  hashtags: string[]
  videoUrl: string | null
  videoAttachmentId?: string | null
  videoStatus?: VideoStatus
  /** Source image used to generate the video. Doubles as a poster while
   *  videoStatus === 'generating'. NOT published to YouTube — only the final
   *  video is. */
  imageUrl?: string | null
  imageAttachmentId?: string | null
  visibility: 'public' | 'unlisted' | 'private'
  youtubePublish?: YouTubePublishMetadata
}

// ─── Pinterest Types ────────────────────────────────────────────────────────

export interface PinterestPublishMetadata extends BasePublishMetadata {
  pinId?: string
  permalink?: string
  mediaType?: 'image' | 'video'
}

export interface PinterestPinContent extends BaseCreativeContent {
  title: string
  description: string
  link: string | null
  imageUrl: string | null
  imageAttachmentId?: string | null
  imageRatio?: number
  imagePrompt: string | null
  altText: string
  videoUrl?: string | null
  videoAttachmentId?: string | null
  videoStatus?: VideoStatus
  pinterestPublish?: PinterestPublishMetadata
}

// ─── Content Map ────────────────────────────────────────────────────────────

export type CreativeContentMap = {
  [CreativeType.InstagramPost]: InstagramPostContent
  [CreativeType.FacebookPost]: FacebookPostContent
  [CreativeType.TikTokVideo]: TikTokVideoContent
  [CreativeType.LinkedInPost]: LinkedInPostContent
  [CreativeType.XPost]: XPostContent
  [CreativeType.YouTubeShort]: YouTubeShortsContent
  [CreativeType.PinterestPin]: PinterestPinContent
}

// ─── Creative Entity ────────────────────────────────────────────────────────

export interface Creative<T extends CreativeType = CreativeType> {
  id: string
  type: T
  name: string
  status: CreativeStatus
  content: CreativeContentMap[T]
  tags: string[]
  thumbnailUrl: string | null
  isSuggestion?: boolean
  /**
   * Generation status for a suggestion creative, written by the server
   * (`data.suggestionStatus`): `'generating'` on creation, then `'ready'`
   * once content lands or `'failed'` if the agent gives up (empty content /
   * error). A `'failed'` card has no media and must surface a Retry affordance
   * instead of an eternal shimmer. `undefined` for non-suggestion creatives.
   */
  suggestionStatus?: 'generating' | 'ready' | 'failed'
  scheduledAt?: string | null
  /** Set once this creative has published to its platform — gates rescheduling. */
  publishedAt?: string | null
  autopilotId?: string | null
  /**
   * Parent Project's ID, when this creative is grouped under one. Posts (kind=post)
   * carry every sibling under one projectId; legacy multi-platform Projects do too.
   * Null for standalone single-platform creatives created from Assistant Chat.
   */
  projectId?: string | null
  /**
   * Set when the user hand-edited this platform variant (server
   * `data.pinnedAt`). A pinned variant stops re-deriving from the post's
   * canonical — the composer shows the "Customized" treatment for it.
   */
  pinnedAt?: string | null
  /**
   * The canonical contentRevision this variant was last derived from (server
   * `data.derivedFromRevision`). Compared against the post's contentRevision
   * to surface "behind the post" on pinned variants. Absent on legacy
   * (pre-propagation) siblings, which never auto-re-derive.
   */
  derivedFromRevision?: number | null
  /**
   * Stamped by the server when an autopilot publish permanently fails
   * (`creative.data.publishFailure`). Drives the failure chip + popover on
   * every surface a post appears (calendar, cockpit, PostModal, Home). The
   * `reason` is a machine token from the server classifier
   * (`token_expired`, `no_channel`, `account_restricted`, `image_error`,
   * `rate_limited`, `app_unaudited`, `unknown`, etc.) — render it through
   * `getPublishFailureCopy` so the UI never leaks the raw token. Cleared by
   * a successful publish or an explicit re-schedule (server lifecycle).
   */
  publishFailure?: { error: string; failedAt: string; reason: string }
  createdAt: string
  updatedAt: string
}

// ─── Default Content (empty state per type) ─────────────────────────────────

export const defaultContent: CreativeContentMap = {
  [CreativeType.InstagramPost]: {
    caption: '',
    hashtags: [],
    imageUrl: null,
    imagePrompt: null,
    format: 'square',
    altText: '',
  },
  [CreativeType.FacebookPost]: {
    message: '',
    imageUrl: null,
    imagePrompt: null,
    altText: '',
  },
  [CreativeType.TikTokVideo]: {
    caption: '',
    hashtags: [],
    videoUrl: null,
  },
  [CreativeType.LinkedInPost]: {
    caption: '',
    hashtags: [],
    imageUrl: null,
    imagePrompt: null,
    format: 'square',
    altText: '',
  },
  [CreativeType.XPost]: {
    text: '',
    hashtags: [],
    imageUrl: null,
    imagePrompt: null,
  },
  [CreativeType.YouTubeShort]: {
    title: '',
    description: '',
    hashtags: [],
    videoUrl: null,
    visibility: 'public',
  },
  [CreativeType.PinterestPin]: {
    title: '',
    description: '',
    link: null,
    imageUrl: null,
    imagePrompt: null,
    altText: '',
  },
}

// ─── Creative Type Metadata ─────────────────────────────────────────────────

export interface CreativeTypeMeta {
  type: CreativeType
  label: string
  description: string
  icon: string // lucide icon name
  color: string // tailwind color class
  placeholderPrompt: string
  examplePrompts: string[]
}

export const creativeTypeMeta: Record<CreativeType, CreativeTypeMeta> = {
  [CreativeType.InstagramPost]: {
    type: CreativeType.InstagramPost,
    label: 'Instagram Post',
    description: 'Image + caption + hashtags',
    icon: 'Instagram',
    color: 'pink',
    placeholderPrompt: 'Describe the Instagram post you want to create...',
    examplePrompts: [
      "A Valentine's Day post for my bakery with heart-shaped pastries",
      'Behind-the-scenes look at our coffee roasting process',
      'New product announcement for our summer collection',
    ],
  },
  [CreativeType.FacebookPost]: {
    type: CreativeType.FacebookPost,
    label: 'Facebook Post',
    description: 'Image + message for your Page',
    icon: 'Facebook',
    color: 'blue',
    placeholderPrompt: 'Describe the Facebook post you want to create...',
    examplePrompts: [
      'A weekend sale announcement for my bakery with fresh bread photos',
      'Share our new menu items with a mouth-watering image',
      'A community event promotion for our local business',
    ],
  },
  [CreativeType.TikTokVideo]: {
    type: CreativeType.TikTokVideo,
    label: 'TikTok Video',
    description: 'Short video + caption + hashtags',
    icon: 'Video',
    color: 'cyan',
    placeholderPrompt: 'Describe the TikTok video you want to create...',
    examplePrompts: [
      'A quick behind-the-scenes of our kitchen prep',
      'Product showcase for our new summer collection',
      'A day in the life at our coffee shop',
    ],
  },
  [CreativeType.LinkedInPost]: {
    type: CreativeType.LinkedInPost,
    label: 'LinkedIn Post',
    description: 'Professional post + image + hashtags',
    icon: 'Linkedin',
    color: 'blue',
    placeholderPrompt: 'Describe the LinkedIn post you want to create...',
    examplePrompts: [
      'A thought leadership post about industry trends in our field',
      'Announce a new team hire or company milestone',
      'Share insights from a recent conference or event',
    ],
  },
  [CreativeType.XPost]: {
    type: CreativeType.XPost,
    label: 'X Post',
    description: 'Tweet + image + hashtags',
    icon: 'Twitter',
    color: 'slate',
    placeholderPrompt: 'Describe the X post you want to create...',
    examplePrompts: [
      'A quick announcement about our new product launch',
      'Share a tip or insight related to our industry',
      'Promote an upcoming event or sale with a catchy hook',
    ],
  },
  [CreativeType.YouTubeShort]: {
    type: CreativeType.YouTubeShort,
    label: 'YouTube Short',
    description: 'Short video + title + description + hashtags',
    icon: 'Youtube',
    color: 'red',
    placeholderPrompt: 'Describe the YouTube Short you want to create...',
    examplePrompts: [
      'A quick product demo for our new line',
      'Behind-the-scenes look at our daily operations',
      'A fun tip or trick related to our industry',
    ],
  },
  [CreativeType.PinterestPin]: {
    type: CreativeType.PinterestPin,
    label: 'Pinterest Pin',
    description: 'Image pin + title + description + link',
    icon: 'Pin',
    color: 'red',
    placeholderPrompt: 'Describe the Pinterest pin you want to create...',
    examplePrompts: [
      'A recipe pin for our best-selling chocolate cake',
      'DIY home decor inspiration featuring our products',
      'A seasonal style guide with outfit ideas from our collection',
    ],
  },
}

// ─── Conversation Types ─────────────────────────────────────────────────────

export type ConversationRole = 'user' | 'assistant' | 'system'

export interface ConversationMessage {
  id: string
  role: ConversationRole
  content: string
  toolCalls?: ToolCall[]
  createdAt: string
}

export interface ToolCall {
  id: string
  toolName: string
  args: Record<string, unknown>
  result?: Record<string, unknown>
  state: 'pending' | 'complete'
}

// ─── Brand Kit ──────────────────────────────────────────────────────────────

// Re-exported from @mondaygpt/constants — single source of truth
import type { BrandImageTag, ToneOfVoice } from '@mondaygpt/constants'
import type { BrandImage as SharedBrandImage } from '@mondaygpt/types'
export { VALID_TONES } from '@mondaygpt/constants'
export type { BrandImageTag, ToneOfVoice }

export interface BrandImage extends SharedBrandImage {
  publicUrl?: string
}

export interface BrandKitChannels {
  instagram?: {
    handle?: string
    goals?: string[]
    profilePicture?: string | null
  }
  facebook?: {
    handle?: string
  }
}

export interface BrandKit {
  businessName: string
  industry: string
  description: string
  targetAudience: string
  toneOfVoice: ToneOfVoice[]
  colors: {
    primary: string
    secondary: string
    accent: string
  }
  logoId: string | null
  instagramProfilePictureId: string | null
  fonts: {
    heading: string
    body: string
  }
  keywords: string[]
  channels: BrandKitChannels
  goals: string[]
  websiteUrl: string | null
  images: BrandImage[]
  /** What the business primarily posts about (products / people / other). Null until classified. */
  contentFocus: BrandImageTag | null
  /**
   * IANA timezone for scheduled posts (e.g. `America/New_York`). Null until
   * captured — the calendar/composer auto-detect from the browser the first
   * time the user opens them, then this is editable from the Brand Kit page.
   */
  timezone: string | null
}
